import { DarkModeRounded, LightMode } from "@mui/icons-material";
import { Box, IconButton, useColorScheme } from "@mui/joy";
import type { IconButtonProps } from "@mui/joy/IconButton";
import { useEffect, useState } from "react";

type ColorSchemeToggleProps = IconButtonProps & {
  prefixText?: string;
};

export function ColorSchemeToggle(props: ColorSchemeToggleProps) {
  const { onClick, sx, prefixText, ...other } = props;
  const { colorScheme, setColorScheme } = useColorScheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  const handleToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (colorScheme === "light") {
      setColorScheme("dark");
    } else {
      setColorScheme("light");
    }

    onClick?.(event);
  };

  if (!mounted) {
    return prefixText ? (
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          opacity: 0.5,
        }}
      >
        {prefixText && (
          <Box sx={{ fontSize: "sm", color: "text.secondary" }}>
            {prefixText}
          </Box>
        )}
        <IconButton
          size="sm"
          variant="plain"
          color="neutral"
          {...other}
          sx={sx}
          disabled
        />
      </Box>
    ) : (
      <IconButton
        size="sm"
        variant="plain"
        color="neutral"
        {...other}
        sx={sx}
        disabled
      />
    );
  }

  if (prefixText) {
    return (
      <Box
        onClick={handleToggle as any}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          cursor: "pointer",
          borderRadius: "sm",
          px: 1,
          py: 0.5,
          transition: "background-color 0.2s",
          "&:hover": {
            backgroundColor: "neutral.softHoverBg",
          },
          "&:active": {
            backgroundColor: "neutral.softActiveBg",
          },
        }}
      >
        <Box sx={{ fontSize: "sm", color: "text.secondary" }}>{prefixText}</Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 32,
            height: 32,
          }}
        >
          {colorScheme === "dark" ? <LightMode /> : <DarkModeRounded />}
        </Box>
      </Box>
    );
  }

  return (
    <IconButton
      data-screenshot="toggle-mode"
      size="sm"
      variant="plain"
      color="neutral"
      {...other}
      onClick={handleToggle}
      sx={[
        colorScheme === "dark"
          ? { "& > *:first-child": { display: "none" } }
          : { "& > *:first-child": { display: "initial" } },
        colorScheme === "light"
          ? { "& > *:last-child": { display: "none" } }
          : { "& > *:last-child": { display: "initial" } },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
    >
      <DarkModeRounded />
      <LightMode />
    </IconButton>
  );
}
